// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { buildAccountRoleOptions } from '../../../../core/utils/accountRoleOptions.ts'
import {
  mapColumnToInputManagerField,
  mapValidationErrors,
  handleAutoPopulation,
  reportingModuleKeyToTableInputManagerModule,
  type TableInputManagerModule
} from '../../../shared/table_input_manager_mappers/index.ts'
import { updateContactRecord } from '../../../../state_manager/state_update_actions/contacts/index.ts'
import { updateCompanyRecord } from '../../../../state_manager/state_update_actions/companies/index.ts'
import { FieldTypeEnum } from '../../../../core/components/InputManager/types.ts'
import { ContactRecordFields, type ContactRecord } from '../../../../state_manager/data_models/contact_record.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { createTableColumns } from '../../../../ui_configs/table_grid_columns.ts'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'

import TableView from '../../../../core/components/TableView/TableView.tsx'
import type { FilterConfig } from '../../../../core/components/TableView/TableViewTypes.ts'

// Record ids are uuid strings; a numeric id (legacy rows) is stringified the way the PATCH URL would anyway.
const toId = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : undefined

interface ContactsTableProps {
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  currentPage: number
  cellEditErrors: Record<string, string>
  activeFilters: any[]
  activeSorting: any
  filterOptions: any
  filterOptionsLoading: boolean
  onLoadMore: (page: number, pageSize: number) => void
  onFiltersChange: (filters: FilterConfig) => void
  onSortChange: (sortConfig: any) => void
  /** InputManager's link onClick: (key, value, recordId) */
  onContactNameClick: (key: string, value: any, contactId: string | number) => void
  onContactSecondaryNameClick: (key: string, value: any, contactId: string | number) => void
  getLookupLabel: (lookupId: string) => string
  getLookupOptions: (category: string) => any[]
  /** Column definitions from global configs (`reporting_column_defaults` + per-report `reporting_columns`) + user prefs. */
  gridColumns: ViewConfigColumn[]
}

/**
 * Contacts table component with virtualized scrolling and editing capabilities
 */
const ContactsTable: React.FC<ContactsTableProps> = ({
  loading,
  loadingMore,
  hasMore,
  currentPage,
  cellEditErrors,
  activeFilters,
  activeSorting,
  filterOptions,
  filterOptionsLoading,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onContactNameClick,
  onContactSecondaryNameClick,
  getLookupLabel,
  getLookupOptions,
  gridColumns
}) => {
  const dispatch = useDispatch()

  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])
  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const fundsData = useSelector((state: any) => state.globalConfigs?.funds || [])

  const contactsData = useSelector((state: any) => state.contacts?.data || [])

  const { getCurrencyById } = useCurrencies()

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getFundOptions = useCallback(() => {
    return fundsData.map((fund: { id: string; name?: string }) => ({
      value: fund.id,
      label: fund.name || ''
    }))
  }, [fundsData])

  const finalizeContactGridAfterPatch = useCallback(
    (record: ContactRecord, inputManagerModule: TableInputManagerModule, updates: Record<string, any>) => {
      const u = { ...updates }
      if (ContactRecordFields.contacts__address_country_code in u) {
        const countryCode = u[ContactRecordFields.contacts__address_country_code]
        if (countryCode) {
          u[ContactRecordFields.contacts__address_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          u[ContactRecordFields.contacts__address_country_label] = null
        }
      }

      if (
        u[ContactRecordFields.contacts__address_city_code] &&
        !u[ContactRecordFields.contacts__address_city_label]
      ) {
        const currentRecord = contactsData.find(
          (contact: ContactRecord) =>
            contact[ContactRecordFields.contacts__id] === record[ContactRecordFields.contacts__id]
        )
        if (currentRecord && currentRecord[ContactRecordFields.contacts__address_city_label]) {
          u[ContactRecordFields.contacts__address_city_label] =
            currentRecord[ContactRecordFields.contacts__address_city_label]
        }
      }

      if (inputManagerModule === 'companies') {
        const companyId =
          (record as Record<string, string | undefined>).accounts__id ||
          record[ContactRecordFields.contact_accounts__account_id]
        if (companyId) {
          dispatch(updateCompanyRecord({ companyId: String(companyId), updates: { ...u } }))
        }
      }

      dispatch(
        updateContactRecord({
          contactId: String(record[ContactRecordFields.contacts__id]),
          updates: u
        })
      )
    },
    [dispatch, countriesData, contactsData]
  )

  const renderInputManagerColumn = useCallback(
    (
      record: ContactRecord,
      columnKey: string,
      fieldId: string,
      fieldType: FieldTypeEnum,
      editingContext: any,
      columnInfo: any = {},
      action?: any
    ) => {
      const isRequired = columnInfo.required || false
      const columnTitle = columnInfo.title || columnKey
      const isColumnEditable = columnInfo.editable !== false

      const updatedRecord =
        contactsData?.find(
          (contact: ContactRecord) =>
            contact[ContactRecordFields.contacts__id] === record[ContactRecordFields.contacts__id]
        ) || record

      const reportingModuleKey = columnInfo.moduleKey as string | undefined
      const inputManagerModule: TableInputManagerModule = reportingModuleKeyToTableInputManagerModule(
        reportingModuleKey,
        'contacts'
      )

      const contactsHelpers = {
        getLookupLabel,
        getLookupOptions
      }
      const companiesHelpers = {
        getCurrencyById,
        getLookupOptions,
        getRoleOptions,
        getFundOptions
      }

      const helpers = inputManagerModule === 'companies' ? companiesHelpers : contactsHelpers

      const fieldConfig = {
        ...mapColumnToInputManagerField(
          inputManagerModule,
          { dataIndex: columnKey, title: columnTitle, required: isRequired, editable: isColumnEditable },
          updatedRecord,
          helpers,
          fieldType
        ),
        key: `${record[ContactRecordFields.contacts__id]}-${columnKey}`
      }

      const validationRecordId =
        inputManagerModule === 'companies'
          ? String((record as { accounts__id?: string }).accounts__id || record[ContactRecordFields.contacts__id] || '')
          : record[ContactRecordFields.contacts__id] || ''

      const validationErrors = mapValidationErrors(inputManagerModule, cellEditErrors, validationRecordId)

      const handleFieldChange = (fieldKey: string, newValue: any) => {
        handleAutoPopulation(
          inputManagerModule,
          fieldKey,
          newValue,
          record,
          (key: string, value: any) => {
            if (editingContext.onCellDataChange) {
              editingContext.onCellDataChange(value)
            }
          },
          helpers
        )

        if (editingContext.onCellDataChange) {
          editingContext.onCellDataChange(newValue)
        }
      }

      const handleFieldEdit = (isEditing: boolean) => {
        if (editingContext?.onFieldEdit) {
          editingContext.onFieldEdit(columnKey, isEditing)
        }
      }

      const handleFieldError = (fieldKey: string, error: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Error in field ${fieldKey}:`, error)
        }
      }

      const getFieldAlignment = () => {
        if (
          fieldType === FieldTypeEnum.CURRENCY ||
          fieldType === FieldTypeEnum.NUMBER ||
          fieldType === FieldTypeEnum.VALUATION
        ) {
          return 'right'
        }
        return 'left'
      }

      const needsTruncation = true

      return (
        <div
          style={{
            width: '100%',
            ...(needsTruncation
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              : {})
          }}
        >
          <InputManager
            inputMode='read'
            labelPosition='none'
            alignment={getFieldAlignment()}
            fields={[fieldConfig]}
            onChange={handleFieldChange}
            onSave={() => {}}
            onError={handleFieldError}
            onPatchSuccess={(_rid, updates) =>
              finalizeContactGridAfterPatch(record, inputManagerModule, updates)
            }
            onFieldEdit={handleFieldEdit}
            enableAutoPatch={true}
            patchId={toId(record[fieldId])}
            recordId={record[ContactRecordFields.contacts__id] || ''}
            validationErrors={validationErrors}
            className='cell-edit-input-manager'
            onClick={action}
            textClassName={needsTruncation ? 'truncate' : undefined}
          />
        </div>
      )
    },
    [
      cellEditErrors,
      getLookupLabel,
      getLookupOptions,
      getCurrencyById,
      getRoleOptions,
      getFundOptions,
      finalizeContactGridAfterPatch,
      contactsData
    ]
  )

  // Create refs for callbacks to prevent unnecessary rerenders
  const handlePrimaryActionRef = useRef(onContactNameClick)
  const handleSecondaryActionRef = useRef(onContactSecondaryNameClick)
  const renderInputManagerColumnRef = useRef(renderInputManagerColumn)

  // Update refs whenever callbacks change
  useEffect(() => {
    handlePrimaryActionRef.current = onContactNameClick
  }, [onContactNameClick])

  useEffect(() => {
    handleSecondaryActionRef.current = onContactSecondaryNameClick
  }, [onContactSecondaryNameClick])

  useEffect(() => {
    renderInputManagerColumnRef.current = renderInputManagerColumn
  }, [renderInputManagerColumn])

  const { columns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      handlePrimaryActionRef,
      handleSecondaryActionRef,
      handleTasksClickRef: undefined,
      handleTaskToggleRef: undefined,
      renderInputManagerColumnRef,
      fallbackRecordModuleKey: 'contacts'
    })
  }, [gridColumns])

  const dataSource = useMemo(() => {
    if (!contactsData || contactsData.length === 0) return []

    const seenIds = new Set()
    return contactsData
      .filter((contact: ContactRecord) => {
        const contactId = contact[ContactRecordFields.contacts__id]
        if (!contactId || seenIds.has(contactId)) {
          return false
        }
        seenIds.add(contactId)
        return true
      })
      .map((contact: ContactRecord) => {
        const currentRoles = contact['prog__current_roles'] || []
        const firstRole = Array.isArray(currentRoles) && currentRoles.length > 0 ? currentRoles[0] : null

        return {
          ...contact,
          key: contact[ContactRecordFields.contacts__id],
          id: contact[ContactRecordFields.contacts__id],
          company: firstRole?.[CompanyTeamEntryFields.contact_accounts__account_id] || null,
          category: firstRole?.[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || null,
          title: firstRole?.[CompanyTeamEntryFields.contact_accounts__title] || ''
        }
      })
  }, [contactsData])

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        onLoadMore={onLoadMore}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        enableCellEditing={true}
        currentFilters={(() => {
          return Array.isArray(activeFilters)
            ? activeFilters.reduce((acc: any, filter: any) => {
                acc[filter.key] = filter.value
                return acc
              }, {})
            : activeFilters || {}
        })()}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={
          Array.isArray(activeSorting) && activeSorting.length > 0
            ? { column: activeSorting[0].key, direction: activeSorting[0].direction }
            : null
        }
        frozenColumns={frozenColumns}
      />
    </div>
  )
}

export default ContactsTable
